import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { FilterPillSpec } from "../../components/FilterPills";
import type { Bucket } from "./helpers";
import { datetimeLocalToIso, isoToDatetimeLocal, toIsoTimestamp } from "./helpers";
import { DATA_START_DATE } from "./sql";

export type Filters = {
  // Full ISO timestamps. The picker rounds presets to second precision; the
  // datetime-local custom input gives minute precision.
  start: string;
  end: string;
  bucket: Bucket;
  features: ReadonlySet<string>;
  models: ReadonlySet<string>;
  companyExternalId: string | null;
};

export type RangePreset =
  | "30m" | "1h" | "4h" | "12h"
  | "1d"  | "3d" | "7d" | "14d" | "30d" | "90d"
  | "custom";

type ConcretePreset = Exclude<RangePreset, "custom">;

const PRESET_DURATION_MS: Record<ConcretePreset, number> = {
  "30m": 30 * 60 * 1000,
  "1h":   1 * 60 * 60 * 1000,
  "4h":   4 * 60 * 60 * 1000,
  "12h": 12 * 60 * 60 * 1000,
  "1d":   1 * 24 * 60 * 60 * 1000,
  "3d":   3 * 24 * 60 * 60 * 1000,
  "7d":   7 * 24 * 60 * 60 * 1000,
  "14d": 14 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
  "90d": 90 * 24 * 60 * 60 * 1000,
};

const PRESET_LABEL: Record<ConcretePreset, string> = {
  "30m": "Past 30 minutes",
  "1h":  "Past 1 hour",
  "4h":  "Past 4 hours",
  "12h": "Past 12 hours",
  "1d":  "Past 1 day",
  "3d":  "Past 3 days",
  "7d":  "Past 7 days",
  "14d": "Past 14 days",
  "30d": "Past 30 days",
  "90d": "Past 90 days",
};

const PRESET_ORDER: ConcretePreset[] = [
  "30m", "1h", "4h", "12h",
  "1d",  "3d", "7d", "14d", "30d", "90d",
];

// Default bucket per preset — short windows want fine granularity, long
// windows want coarse so the chart stays readable.
const PRESET_DEFAULT_BUCKET: Record<ConcretePreset, Bucket> = {
  "30m": "hourly",
  "1h":  "hourly",
  "4h":  "hourly",
  "12h": "hourly",
  "1d":  "hourly",
  "3d":  "hourly",
  "7d":  "daily",
  "14d": "daily",
  "30d": "daily",
  "90d": "weekly",
};

const DATA_START_TS = `${DATA_START_DATE}T00:00:00Z`;

export function presetRange(preset: ConcretePreset, now: Date): { start: string; end: string } {
  const end = now.toISOString();
  const startMs = now.getTime() - PRESET_DURATION_MS[preset];
  const dataStartMs = new Date(DATA_START_TS).getTime();
  const start = new Date(Math.max(startMs, dataStartMs)).toISOString();
  return { start, end };
}

// Detect which preset matches the current filter window. We use a ±60s
// tolerance because "now" drifts every render; without it, the preset would
// flip to "custom" the moment the user opens the page.
export function detectPreset(f: Filters, now: Date): RangePreset {
  const tolerance = 60_000;
  const endMs = new Date(f.end).getTime();
  if (Math.abs(endMs - now.getTime()) > tolerance) return "custom";
  const startMs = new Date(f.start).getTime();
  for (const p of PRESET_ORDER) {
    const expected = now.getTime() - PRESET_DURATION_MS[p];
    if (Math.abs(startMs - expected) <= tolerance) return p;
  }
  // The "all" case: start clamped to data-start, end at now, and the data-start
  // distance doesn't match any preset window.
  if (Math.abs(startMs - new Date(DATA_START_TS).getTime()) <= tolerance) {
    return "custom";
  }
  return "custom";
}

export function filtersToPills(
  f: Filters,
  options: {
    companyOptions: { key: string; label: string }[];
    onClearCompany: () => void;
    onClearFeatures: () => void;
    onClearModels: () => void;
  },
): FilterPillSpec[] {
  const pills: FilterPillSpec[] = [];

  const startLabel = formatTs(f.start);
  const endLabel   = formatTs(f.end);
  pills.push({ kind: "range", label: `${startLabel} to ${endLabel}` });

  const BUCKET_LABEL: Record<Bucket, string> = {
    hourly:  "Hourly",
    daily:   "Daily",
    weekly:  "Weekly",
    monthly: "Monthly",
  };
  pills.push({ kind: "bucket", label: BUCKET_LABEL[f.bucket] });

  if (f.features.size > 0) {
    pills.push({
      kind: "selection",
      label: pluralise(f.features.size, "feature"),
      detail: [...f.features],
      onClear: options.onClearFeatures,
    });
  }
  if (f.models.size > 0) {
    pills.push({
      kind: "selection",
      label: pluralise(f.models.size, "model"),
      detail: [...f.models],
      onClear: options.onClearModels,
    });
  }
  if (f.companyExternalId) {
    const opt = options.companyOptions.find((o) => o.key === f.companyExternalId);
    pills.push({
      kind: "selection",
      label: opt?.label ?? f.companyExternalId,
      onClear: options.onClearCompany,
    });
  }

  return pills;
}

function pluralise(n: number, word: string): string {
  return n === 1 ? `1 ${word}` : `${n} ${word}s`;
}

function formatTs(iso: string): string {
  const d = new Date(iso);
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const hh  = String(d.getHours()).padStart(2, "0");
  const mm  = String(d.getMinutes()).padStart(2, "0");
  return `${month} ${day}, ${hh}:${mm}`;
}

const chipShell: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "var(--bg-2)",
  border: "1px solid var(--border-default)",
  borderRadius: 6,
  padding: "4px 10px",
  fontSize: 12,
};

const chipLabel: CSSProperties = {
  color: "var(--fg-3)",
  fontWeight: 500,
};

const chipControl: CSSProperties = {
  appearance: "auto",
  background: "transparent",
  border: "none",
  padding: 0,
  font: "inherit",
  color: "var(--fg-1)",
  fontWeight: 600,
  cursor: "pointer",
  outline: "none",
};

export function FiltersBar({
  filters, onChange, today, featureOptions, modelOptions, companyOptions,
}: {
  filters: Filters;
  onChange: (next: Filters) => void;
  today: Date;
  featureOptions: string[];
  modelOptions: string[];
  companyOptions: { key: string; label: string }[];
}) {
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    onChange({ ...filters, [key]: value });

  return (
    <section className="filterbar">
      <span className="filterbar-label"><i className="ti ti-filter" />Filters</span>

      <div className="filterbar-stack">
        <div className="filterbar-row">
          <DateRangePicker filters={filters} onChange={onChange} today={today} />

          <NativeSelect
            label="Bucket"
            value={filters.bucket}
            onChange={(v) => set("bucket", v as Bucket)}
            options={[
              { value: "hourly",  label: "Hourly" },
              { value: "daily",   label: "Daily" },
              { value: "weekly",  label: "Weekly" },
              { value: "monthly", label: "Monthly" },
            ]}
          />

          <MultiSelectChip
            label="Features"
            options={featureOptions}
            selected={filters.features}
            onChange={(next) => set("features", next)}
            allLabel="All features"
          />

          <MultiSelectChip
            label="Models"
            options={modelOptions}
            selected={filters.models}
            onChange={(next) => set("models", next)}
            allLabel="All models"
          />

          <SearchableSingleSelect
            label="Company"
            options={companyOptions}
            selectedKey={filters.companyExternalId}
            onChange={(key) => set("companyExternalId", key)}
            allLabel="All companies"
          />
        </div>
      </div>
    </section>
  );
}

function DateRangePicker({
  filters, onChange, today,
}: {
  filters: Filters;
  onChange: (next: Filters) => void;
  today: Date;
}) {
  const [open, setOpen]       = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const preset = detectPreset(filters, today);
  const summary = preset === "custom"
    ? `${formatTs(filters.start)} to ${formatTs(filters.end)}`
    : PRESET_LABEL[preset];

  const pickPreset = (p: ConcretePreset) => {
    const range = presetRange(p, today);
    onChange({
      ...filters,
      start: range.start,
      end: range.end,
      bucket: PRESET_DEFAULT_BUCKET[p],
    });
    setShowCustom(false);
    setOpen(false);
  };

  return (
    <span className="filter-chip month-multi" ref={ref}>
      <i className="ti ti-calendar" style={{ fontSize: 12, color: "var(--fg-3)" }} />
      <span
        className="chip-value"
        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}
      >
        {summary}
      </span>
      <i className="ti ti-chevron-down chevron" />
      <button
        type="button"
        className="chip-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      />
      {open && (
        <div
          className="month-multi-popover"
          role="listbox"
          onClick={(e) => e.stopPropagation()}
          style={{ minWidth: 280, maxHeight: 480, padding: 4 }}
        >
          <div style={{ fontSize: 10, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: 0.5, padding: "6px 10px 4px" }}>
            Quick presets
          </div>
          {PRESET_ORDER.map((p) => (
            <button
              key={p}
              type="button"
              className="row"
              onClick={() => pickPreset(p)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                background: preset === p ? "var(--bg-3)" : "transparent",
                border: "none",
                cursor: "pointer",
                padding: "6px 10px",
                fontSize: 12,
                color: "var(--fg-1)",
                fontWeight: preset === p ? 700 : 500,
                textAlign: "left",
              }}
            >
              <span>{PRESET_LABEL[p]}</span>
              <span style={{ color: "var(--fg-3)", fontSize: 11 }}>{p}</span>
            </button>
          ))}
          <div className="divider" />
          <button
            type="button"
            className="row"
            onClick={() => setShowCustom((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              width: "100%",
              background: showCustom || preset === "custom" ? "var(--bg-3)" : "transparent",
              border: "none",
              cursor: "pointer",
              padding: "6px 10px",
              fontSize: 12,
              color: "var(--fg-1)",
              fontWeight: showCustom || preset === "custom" ? 700 : 500,
              textAlign: "left",
            }}
          >
            <i className="ti ti-clock-edit" style={{ fontSize: 13 }} />
            Custom range
            <i
              className="ti ti-chevron-down"
              style={{
                marginLeft: "auto",
                transform: showCustom || preset === "custom" ? "rotate(180deg)" : undefined,
                transition: "transform 0.15s",
              }}
            />
          </button>
          {(showCustom || preset === "custom") && (
            <div style={{ padding: "8px 10px 4px", display: "flex", flexDirection: "column", gap: 6 }}>
              <CustomDatetimeInput
                label="From"
                value={filters.start}
                max={filters.end}
                onChange={(v) => onChange({ ...filters, start: v })}
              />
              <CustomDatetimeInput
                label="To"
                value={filters.end}
                min={filters.start}
                onChange={(v) => onChange({ ...filters, end: v })}
              />
            </div>
          )}
        </div>
      )}
    </span>
  );
}

function CustomDatetimeInput({
  label, value, min, max, onChange,
}: {
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (next: string) => void;
}) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
      <span style={{ color: "var(--fg-3)", fontWeight: 600, minWidth: 36 }}>{label}</span>
      <input
        type="datetime-local"
        value={isoToDatetimeLocal(value)}
        min={min ? isoToDatetimeLocal(min) : undefined}
        max={max ? isoToDatetimeLocal(max) : undefined}
        onChange={(e) => {
          if (!e.target.value) return;
          onChange(datetimeLocalToIso(e.target.value));
        }}
        style={{
          flex: 1,
          padding: "4px 6px",
          fontSize: 11,
          border: "1px solid var(--border-default)",
          borderRadius: 4,
          fontFamily: "inherit",
        }}
      />
    </label>
  );
}

function NativeSelect({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label style={chipShell}>
      <span style={chipLabel}>{label}:</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={chipControl}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function MultiSelectChip({
  label, options, selected, onChange, allLabel,
}: {
  label: string;
  options: string[];
  selected: ReadonlySet<string>;
  onChange: (next: ReadonlySet<string>) => void;
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const display = selected.size === 0
    ? allLabel
    : selected.size === 1
      ? [...selected][0]!
      : `${selected.size} selected`;

  const lower = query.trim().toLowerCase();
  const filtered = lower
    ? options.filter((o) => o.toLowerCase().includes(lower))
    : options;

  const toggle = (opt: string) => {
    const next = new Set(selected);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    onChange(next);
  };

  return (
    <span className="filter-chip month-multi" ref={ref} style={{ minWidth: 180 }}>
      <span className="chip-label">{label}:</span>
      <span
        className="chip-value"
        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}
      >
        {display}
      </span>
      <i className="ti ti-chevron-down chevron" />
      <button
        type="button"
        className="chip-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      />
      {open && (
        <div
          className="month-multi-popover"
          role="listbox"
          onClick={(e) => e.stopPropagation()}
          style={{ minWidth: 240, maxHeight: 360 }}
        >
          <input
            type="text"
            placeholder={`Search ${label.toLowerCase()}...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 8px",
              fontSize: 12,
              border: "1px solid var(--border-default)",
              borderRadius: 4,
              marginBottom: 6,
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
          <label className="row" onClick={() => onChange(new Set())}>
            <input type="checkbox" checked={selected.size === 0} readOnly />
            <span>{allLabel}</span>
          </label>
          <div className="divider" />
          {filtered.slice(0, 200).map((o) => (
            <label key={o} className="row" onClick={() => toggle(o)}>
              <input type="checkbox" checked={selected.has(o)} readOnly />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o}</span>
            </label>
          ))}
          {filtered.length > 200 && (
            <div style={{ fontSize: 11, color: "var(--fg-3)", padding: "4px 10px" }}>
              Showing first 200 of {filtered.length} matches.
            </div>
          )}
        </div>
      )}
    </span>
  );
}

function SearchableSingleSelect({
  label, options, selectedKey, onChange, allLabel,
}: {
  label: string;
  options: { key: string; label: string }[];
  selectedKey: string | null;
  onChange: (key: string | null) => void;
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = selectedKey ? options.find((o) => o.key === selectedKey) : null;
  const display = selected ? selected.label : allLabel;
  const lower = query.trim().toLowerCase();
  const filtered = lower
    ? options.filter((o) => o.label.toLowerCase().includes(lower))
    : options;

  return (
    <span className="filter-chip month-multi" ref={ref} style={{ minWidth: 220 }}>
      <span className="chip-label">{label}:</span>
      <span
        className="chip-value"
        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}
      >
        {display}
      </span>
      <i className="ti ti-chevron-down chevron" />
      <button
        type="button"
        className="chip-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      />
      {open && (
        <div
          className="month-multi-popover"
          role="listbox"
          onClick={(e) => e.stopPropagation()}
          style={{ minWidth: 280, maxHeight: 360 }}
        >
          <input
            type="text"
            placeholder={`Search ${label.toLowerCase()}...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 8px",
              fontSize: 12,
              border: "1px solid var(--border-default)",
              borderRadius: 4,
              marginBottom: 6,
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
          <label
            className="row"
            onClick={() => { onChange(null); setOpen(false); setQuery(""); }}
          >
            <input type="checkbox" checked={selectedKey === null} readOnly />
            <span>{allLabel}</span>
          </label>
          <div className="divider" />
          {filtered.slice(0, 200).map((o) => (
            <label
              key={o.key}
              className="row"
              onClick={() => { onChange(o.key); setOpen(false); setQuery(""); }}
            >
              <input type="checkbox" checked={selectedKey === o.key} readOnly />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
            </label>
          ))}
          {filtered.length > 200 && (
            <div style={{ fontSize: 11, color: "var(--fg-3)", padding: "4px 10px" }}>
              Showing first 200 of {filtered.length} matches. Narrow your search.
            </div>
          )}
        </div>
      )}
    </span>
  );
}

// Pure helpers for client-side filtering of overview rows by feature/model.
export function overviewMatches(
  row: { name: string; model: string },
  filters: Filters,
): boolean {
  if (filters.features.size > 0 && !filters.features.has(row.name)) return false;
  if (filters.models.size > 0 && !filters.models.has(row.model)) return false;
  return true;
}

// MemoSet helps callers pass a stable empty set to useAdminQuery query keys.
export const EMPTY_SET: ReadonlySet<string> = new Set();

export function defaultFilters(today: Date): Filters {
  // Start narrow on load — a wide default range over 1.88M+ rows can take
  // seconds and hammers the DB on every navigation. Users widen as needed.
  const range = presetRange("1d", today);
  return {
    start: range.start,
    end: range.end,
    bucket: "hourly",
    features: EMPTY_SET,
    models: EMPTY_SET,
    companyExternalId: null,
  };
}

// Used when re-hydrating any filter state that might still be in the legacy
// YYYY-MM-DD format (e.g. a saved URL-state, future addition).
export function migrateFilters(f: Filters): Filters {
  return { ...f, start: toIsoTimestamp(f.start), end: toIsoTimestamp(f.end) };
}
